import React, { useMemo, useState, useContext, useCallback } from 'react';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  ThemeProvider,
  Button,
  IconButton,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Stack,
  CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import HistoryIcon from '@mui/icons-material/History';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AddIcon from '@mui/icons-material/Add';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { MaterialReactTable } from 'material-react-table';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAPI } from '../Hooks/useAPIs';
import { useCurrentUser } from '../Hooks/useCurrentUser';
import { useReusableTable } from '../common/TablePanel/tablePanel';
import { sortingToOrdering } from '../common/TablePanel/tableUtils';
import { useUserDeviceColumns } from './components/DeviceColumns';
import { useUrlSorting } from '../Hooks/useUrlSorting';
import { TimezoneContext } from '../common/TimezoneContext/TimezoneContext';
import FiltersComponent from '../common/FiltersPanel/FIltersPanel';
import RegistrationRequestDialog from '../common/Dialogs/Devices/RegistrationRequestDialog';
import TransferRequestDialog from '../common/Dialogs/Devices/TransferRequestDialog';
import ApproveTransferDialog from '../common/Dialogs/Devices/ApproveTransferDialog';
import NewDeviceRequestDialog from '../common/Dialogs/Devices/NewDeviceRequestDialog';
import { sanitizeMacAddress } from '../utils/macAddress';
import AddMultipleDevicesDialog from '../common/Dialogs/Devices/AddMultipleDevicesDialog';
import DeviceRegistrationHistoryDialog from '../common/Dialogs/Devices/DeviceRegistrationHistoryDialog';
import DeviceNotesDialog from '../common/Dialogs/Devices/DeviceNotesDialog';
import { RegistrationCard } from './components/RegistrationCard';
import './DeviceView.css';

const REQUEST_TYPE_OPTIONS = [
  { value: '', label: 'All Request Types' },
  { value: 'ownership_transfer', label: 'Ownership Transfer' },
  { value: 'new_device', label: 'New Device' },
  { value: 'registration_renewal', label: 'Registration Renewal' },
  { value: 'tailscale_approval', label: 'Tailscale Approval' },
  { value: 'device_lost', label: 'Device Lost' },
  { value: 'device_replacement', label: 'Device Replacement' },
  { value: 'device_return', label: 'Device Return' },
];

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const UserDeviceView = ({ activeTheme }) => {
  const [viewMode, setViewMode] = useState('devices');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 100 });
  const [sorting, setSorting] = useUrlSorting([{ id: 'last_activity', desc: true }]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [approveTransferDialogOpen, setApproveTransferDialogOpen] = useState(false);
  const [newDeviceDialogOpen, setNewDeviceDialogOpen] = useState(false);
  const [addMultipleDevicesDialogOpen, setAddMultipleDevicesDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [registrationType, setRegistrationType] = useState(null);
  const [selectedTransferRequest, setSelectedTransferRequest] = useState(null);
  const [pendingRequestTypeFilter, setPendingRequestTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuDevice, setMenuDevice] = useState(null);

  const { apiCall, isMsalReady } = useAPI();
  const { isAuthenticated } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { formatDateTime, userTimezone, handleTimezoneChange } = useContext(TimezoneContext);

  const deviceQuery = useQuery({
    queryKey: ['user-device-data', pagination.pageIndex, pagination.pageSize, sorting],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', (pagination.pageIndex + 1).toString());
      params.append('page_size', pagination.pageSize.toString());
      params.append('my_devices', 'true');
      const ordering = sortingToOrdering(sorting);
      if (ordering) params.append('ordering', ordering);
      const response = await apiCall('GET', `api/v1/devices/query?${params.toString()}`);
      return response.data;
    },
    enabled: isMsalReady && isAuthenticated && viewMode === 'devices',
    placeholderData: keepPreviousData,
  });

  const pendingRegistrationsQuery = useQuery({
    queryKey: ['user-pending-requests', pagination.pageIndex, pagination.pageSize, pendingRequestTypeFilter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) {
        params.append('status', statusFilter);
      }
      params.append('page', (pagination.pageIndex + 1).toString());
      params.append('page_size', pagination.pageSize.toString());
      if (pendingRequestTypeFilter) {
        params.append('request_type', pendingRequestTypeFilter);
      }
      const response = await apiCall('GET', `api/v1/devices/requests?${params.toString()}`);
      return response.data;
    },
    enabled: isMsalReady && isAuthenticated && viewMode === 'pending',
    placeholderData: keepPreviousData,
  });

  const data = useMemo(() => deviceQuery.data?.results || [], [deviceQuery.data]);

  const createDeviceRequestMutation = useMutation({
    mutationFn: async (requestData) => {
      const response = await apiCall('POST', 'api/v1/devices/requests', requestData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device-requests'] });
      queryClient.invalidateQueries({ queryKey: ['user-device-data'] });
      queryClient.invalidateQueries({ queryKey: ['user-pending-requests'] });
      setRegistrationDialogOpen(false);
      setTransferDialogOpen(false);
      setNewDeviceDialogOpen(false);
      setAddMultipleDevicesDialogOpen(false);
      setSelectedDevice(null);
    },
  });

  const approveRequestMutation = useMutation({
    mutationFn: async ({ requestId, notes }) => {
      const response = await apiCall('POST', `api/v1/devices/requests/${requestId}/approve`, { notes });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-device-data'] });
      setApproveTransferDialogOpen(false);
      setSelectedTransferRequest(null);
    },
  });

  const denyRequestMutation = useMutation({
    mutationFn: async ({ requestId, rejection_reason, notes }) => {
      const response = await apiCall('POST', `api/v1/devices/requests/${requestId}/deny`, { 
        rejection_reason: rejection_reason || notes,
        notes 
      });
      return response.data;
    },
    onSuccess: () => {
      setApproveTransferDialogOpen(false);
      setSelectedTransferRequest(null);
    },
  });

  const handleNewDeviceSubmit = (requestData) => {
    const macAddress = sanitizeMacAddress(requestData.mac_addr);

    createDeviceRequestMutation.mutate({
      request_type: 'new_device',
      requested_mac_addr: macAddress,
      reason: requestData.notes || 'New device request',
      notes: requestData.notes,
      ...(requestData.pi && { requested_pi: requestData.pi }),
      ...(requestData.device_type && { requested_device_type: requestData.device_type }),
    });
  };

  const handleMultipleDevicesSubmit = async (requestData) => {
    try {
      await Promise.all(
        requestData.devices.map(device =>
          createDeviceRequestMutation.mutateAsync({
            request_type: 'new_device',
            requested_mac_addr: sanitizeMacAddress(device.mac_addr),
            reason: requestData.notes || 'Bulk device request',
            notes: requestData.notes,
            ...(device.pi && { requested_pi: device.pi }),
            ...(device.device_type && { requested_device_type: device.device_type }),
          })
        )
      );
      setAddMultipleDevicesDialogOpen(false);
    } catch (error) {
      console.error('Failed to create multiple device requests:', error);
    }
  };

  const handleMenuOpen = (event, device) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuDevice(device);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuDevice(null);
  };

  const handleRequestTailscale = (device) => {
    if (device?.is_lost || device?.is_broken) {
      return;
    }
    setSelectedDevice(device);
    setRegistrationType('tailscale_approval');
    setRegistrationDialogOpen(true);
    handleMenuClose();
  };

  const handleRequestTransfer = (device) => {
    if (device?.is_lost || device?.is_broken) {
      return;
    }
    setSelectedDevice(device);
    setTransferDialogOpen(true);
    handleMenuClose();
  };

  const handleViewHistory = (device) => {
    setSelectedDevice(device);
    setHistoryDialogOpen(true);
    handleMenuClose();
  };

  const handleViewNotes = (device) => {
    setSelectedDevice(device);
    setNotesDialogOpen(true);
    handleMenuClose();
  };

  const handleRequestRenewal = (device) => {
    if (device?.is_lost || device?.is_broken) {
      return;
    }
    setSelectedDevice(device);
    setRegistrationType('registration_renewal');
    setRegistrationDialogOpen(true);
    handleMenuClose();
  };

  const handleReportLost = (device) => {
    if (device?.is_lost || device?.is_broken) {
      return;
    }
    setSelectedDevice(device);
    setRegistrationType('device_lost');
    setRegistrationDialogOpen(true);
    handleMenuClose();
  };

  const handleRequestReplacement = (device) => {
    if (device?.is_lost || device?.is_broken) {
      return;
    }
    setSelectedDevice(device);
    setRegistrationType('device_replacement');
    setRegistrationDialogOpen(true);
    handleMenuClose();
  };

  const handleRegistrationSubmit = (requestData) => {
    if (selectedDevice) {
      createDeviceRequestMutation.mutate({
        request_type: requestData.registration_type || registrationType,
        device_id: selectedDevice.id,
        reason: requestData.reason || requestData.notes || `${registrationType} request`,
        notes: requestData.notes,
        ...(requestData.pi && { requested_pi: requestData.pi }),
        ...(requestData.device_type && { requested_device_type: requestData.device_type }),
        ...(requestData.expiry_date && { expiry_date: requestData.expiry_date }),
      });
    }
  };

  const handleTransferSubmit = (requestData) => {
    if (selectedDevice) {
      createDeviceRequestMutation.mutate({
        request_type: 'ownership_transfer',
        device_id: selectedDevice.id,
        requested_to: requestData.transfer_to_user_id,
        reason: requestData.notes || 'Transfer request',
        notes: requestData.notes,
      });
    }
  };

  const handleOpenDetails = useCallback((device) => {
    navigate(`/devices/${device.id}`, {
      state: {
        device,
        returnTo: `${location.pathname}${location.search}`,
      },
    });
  }, [navigate, location.pathname, location.search]);

  const columns = useUserDeviceColumns({ formatDateTime });

  const viewModeSelector = useMemo(() => ({
    viewMode,
    handleViewModeChange: (newMode) => {
      if (newMode !== null) {
        setViewMode(newMode);
        setPagination({ pageIndex: 0, pageSize: 100 });
        if (newMode === 'pending') {
          setPendingRequestTypeFilter('');
          setStatusFilter('pending');
        }
      }
    },
  }), [viewMode]);

  const requestTypeFilter = useMemo(() => ({
    value: pendingRequestTypeFilter,
    onChange: (value) => {
      setPendingRequestTypeFilter(value);
      setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
    },
    options: REQUEST_TYPE_OPTIONS,
  }), [pendingRequestTypeFilter, pagination.pageSize]);

  const statusFilterConfig = useMemo(() => ({
    value: statusFilter,
    onChange: (value) => {
      setStatusFilter(value);
      setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
    },
    options: STATUS_OPTIONS,
  }), [statusFilter, pagination.pageSize]);

  const { table } = useReusableTable({
    columns,
    data,
    rowCount: deviceQuery.data?.count || 0,
    isLoading: deviceQuery.isFetching,
    enableRowSelection: false,
    enableRowExpansion: false,
    enableCompareFiles: false,
    enableDefaultAddToCart: false,
    enableRowVirtualization: true,
    enableEditing: false,
    getRowId: (row) => row.id,
    pagination,
    sorting,
    onSortingChange: setSorting,
    manualPagination: true,
    onPaginationChange: setPagination,
    muiPaginationProps: {
      color: 'primary',
      rowsPerPageOptions: [100, 200, 500, 1000],
    },
    muiTableContainerProps: { sx: { maxHeight: 'calc(100vh - 320px)' } },
    renderTopToolbarCustomActions: () => {
      return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setNewDeviceDialogOpen(true)}
            size="small"
          >
            Add Device
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setAddMultipleDevicesDialogOpen(true)}
            size="small"
          >
            Add Multiple Devices
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['user-device-data'] });
              queryClient.invalidateQueries({ queryKey: ['user-pending-requests'] });
            }}
            size="small"
          >
            Refresh
          </Button>
        </Box>
      );
    },
    renderRowActions: ({ row }) => {
      const device = row.original;
      
      return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Open Details">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpenDetails(device)}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View History">
            <IconButton
              size="small"
              color="default"
              onClick={() => handleViewHistory(device)}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Notes">
            <IconButton
              size="small"
              color="default"
              onClick={() => handleViewNotes(device)}
            >
              <NoteAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="More Actions">
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, device)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
  });

  const renderPendingRegistrations = () => {
    if (viewMode !== 'pending') return null;

    const pendingResults = pendingRegistrationsQuery.data?.results || [];

    return (
      <Paper elevation={2} className="pending-requests-panel">
        <Box className="pending-requests-header">
          <PendingActionsIcon className="pending-requests-header-icon" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            My Requests
          </Typography>
        </Box>
        
        <Divider className="pending-requests-divider" />
        
        {pendingRegistrationsQuery.isFetching && pendingResults.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : pendingResults.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <PendingActionsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Requests Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pendingRequestTypeFilter || statusFilter
                ? `No requests found matching the selected filters.`
                : 'You have no device requests at this time.'}
            </Typography>
          </Box>
        ) : (
          <>
            <Stack spacing={2}>
              {pendingResults.map((registration) => (
                <RegistrationCard
                  key={registration.id}
                  registration={registration}
                  formatDateTime={formatDateTime}
                />
              ))}
            </Stack>
            {pendingRegistrationsQuery.data?.count > pendingResults.length && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {pendingResults.length} of {pendingRegistrationsQuery.data?.count || 0} requests.
                  Use pagination controls below to view more.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    );
  };

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={activeTheme}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error">Please log in to view your devices.</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={activeTheme}>
      <Box className="device-main-container">
        <Box className="device-sidebar">
          <FiltersComponent
            activeTheme={activeTheme}
            timezoneSelector={{ timezone: userTimezone, userTimezone, handleTimezoneChange }}
            viewModeSelector={viewModeSelector}
            requestTypeFilter={requestTypeFilter}
            statusFilter={statusFilterConfig}
          />
        </Box>

        <Box className="device-content">
          {renderPendingRegistrations()}
          {viewMode === 'devices' && (
            <MaterialReactTable table={table} />
          )}
        </Box>
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem 
          onClick={() => menuDevice && handleRequestRenewal(menuDevice)}
          disabled={menuDevice?.is_lost || menuDevice?.is_broken}
        >
          <ListItemIcon>
            <AutorenewIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Renew Registration</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => menuDevice && handleRequestTailscale(menuDevice)}
          disabled={menuDevice?.is_lost || menuDevice?.is_broken}
        >
          <ListItemIcon>
            <VerifiedUserIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText>Renew Tailscale</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => menuDevice && handleRequestTransfer(menuDevice)}
          disabled={menuDevice?.is_lost || menuDevice?.is_broken}
        >
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Transfer Ownership</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => menuDevice && handleRequestReplacement(menuDevice)}
          disabled={menuDevice?.is_lost || menuDevice?.is_broken}
        >
          <ListItemIcon>
            <FindReplaceIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText>Request Replacement</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => menuDevice && handleReportLost(menuDevice)}
          disabled={menuDevice?.is_lost || menuDevice?.is_broken}
        >
          <ListItemIcon>
            <ReportProblemIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Report Lost</ListItemText>
        </MenuItem>
        {(menuDevice?.is_lost || menuDevice?.is_broken) && (
          <>
            <Divider />
            <MenuItem disabled>
              <ListItemText 
                primary={menuDevice?.is_lost ? "Device is lost - requests disabled" : "Device is broken - requests disabled"}
                primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', sx: { fontStyle: 'italic' } }}
              />
            </MenuItem>
          </>
        )}
      </Menu>

      <RegistrationRequestDialog
        open={registrationDialogOpen}
        onClose={() => {
          setRegistrationDialogOpen(false);
          setSelectedDevice(null);
          setRegistrationType(null);
        }}
        device={selectedDevice}
        registrationType={registrationType}
        onSubmit={handleRegistrationSubmit}
        isLoading={createDeviceRequestMutation.isLoading}
        isAdmin={false}
      />

      <TransferRequestDialog
        open={transferDialogOpen}
        onClose={() => {
          setTransferDialogOpen(false);
          setSelectedDevice(null);
        }}
        device={selectedDevice}
        onSubmit={handleTransferSubmit}
        isLoading={createDeviceRequestMutation.isLoading}
      />

      <ApproveTransferDialog
        open={approveTransferDialogOpen}
        onClose={() => {
          setApproveTransferDialogOpen(false);
          setSelectedTransferRequest(null);
        }}
        transferRequest={selectedTransferRequest}
        onApprove={(notes) => {
          if (selectedTransferRequest) {
            approveRequestMutation.mutate({
              requestId: selectedTransferRequest.id,
              notes,
            });
          }
        }}
        onDeny={(notes) => {
          if (selectedTransferRequest) {
            denyRequestMutation.mutate({
              requestId: selectedTransferRequest.id,
              notes,
            });
          }
        }}
        isLoading={approveRequestMutation.isLoading || denyRequestMutation.isLoading}
      />

      <NewDeviceRequestDialog
        open={newDeviceDialogOpen}
        onClose={() => {
          setNewDeviceDialogOpen(false);
        }}
        onSubmit={handleNewDeviceSubmit}
        isLoading={createDeviceRequestMutation.isLoading}
      />

      <AddMultipleDevicesDialog
        open={addMultipleDevicesDialogOpen}
        onClose={() => {
          setAddMultipleDevicesDialogOpen(false);
        }}
        onSubmit={handleMultipleDevicesSubmit}
        isLoading={createDeviceRequestMutation.isLoading}
      />

      <DeviceRegistrationHistoryDialog
        open={historyDialogOpen}
        onClose={() => {
          setHistoryDialogOpen(false);
          setSelectedDevice(null);
        }}
        device={selectedDevice}
      />

      <DeviceNotesDialog
        open={notesDialogOpen}
        onClose={() => {
          setNotesDialogOpen(false);
          setSelectedDevice(null);
        }}
        device={selectedDevice}
      />
    </ThemeProvider>
  );
};

export default UserDeviceView;
